/**
 * Represents a single database migration entry for administrative inspection.
 */
export interface IMigrationAdminItem {
  /** Zero-padded identifier, e.g. "001". Determines execution order. */
  id: string;
  /** Human-readable description of the migration. */
  name: string;
  /** Explains what the migration does and why it exists. */
  reason: string;
  /** Execution status of the migration. */
  status: "complete" | "pending" | "failed";
  /** Epoch timestamp in milliseconds when the migration was applied, if completed or failed. */
  appliedAt?: number;
  /** Execution duration in milliseconds, if tracked. */
  durationMs?: number;
  /** Error message if the migration failed. */
  error?: string;
}

/**
 * Request payload for getDatabaseMigrationsFunc.
 */
export interface IGetDatabaseMigrationsRequest {}

/**
 * Response payload for getDatabaseMigrationsFunc.
 */
export interface IGetDatabaseMigrationsResponse {
  /** Indicates whether the operation succeeded. */
  status: boolean;
  /** List of all registered migrations with their execution status. */
  migrations: IMigrationAdminItem[];
  /** Optional error message if the operation failed. */
  error?: string;
}

/**
 * Request payload for runDatabaseMigrationsFunc.
 */
export interface IRunDatabaseMigrationsRequest {}

/**
 * Execution metrics for a single migration run.
 */
export interface IMigrationRunResult {
  /** Migration identifier. */
  id: string;
  /** Human-readable migration name. */
  name: string;
  /** Execution status ("complete", "skipped", or "failed"). */
  status: string;
  /** Duration in milliseconds. */
  durationMs: number;
}

/**
 * Response payload for runDatabaseMigrationsFunc.
 */
export interface IRunDatabaseMigrationsResponse {
  /** Indicates whether the migration execution batch succeeded. */
  status: boolean;
  /** Summary message describing the execution result. */
  message: string;
  /** Breakdown of individual migration run results. */
  results?: IMigrationRunResult[];
  /** Optional error message if the migration run failed. */
  error?: string;
}

// Aliases for compatibility
export type GetDatabaseMigrationsResponse = IGetDatabaseMigrationsResponse;
export type RunDatabaseMigrationsResponse = IRunDatabaseMigrationsResponse;
export type MigrationAdminItem = IMigrationAdminItem;
